import React, { useRef } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import { Box, Button, Chip, IconButton, Popover, TextField, Typography } from '@mui/material';

import { MAX_COMMENT_LENGTH } from '../constants';
import type { ThumbsFeedbackPresentationalProps } from '../types';

import {
  categoriesRowSx,
  commentRowSx,
  compactThumbButtonSx,
  expansionSx,
  popoverContentSx,
  rootSx,
  thanksSx,
  thumbButtonSx,
  thumbsRowSx,
} from './ThumbsFeedback.styles';

/**
 * Shared detail content (categories + optional comment + submit) used by both
 * inline expansion and popover modes.
 */
const FeedbackDetails: React.FC<
  Pick<
    ThumbsFeedbackPresentationalProps,
    | 'categories'
    | 'selectedCategories'
    | 'comment'
    | 'mode'
    | 'isSubmitting'
    | 'onCategoryToggle'
    | 'onCommentChange'
    | 'onSubmitDetails'
  >
> = ({
  categories,
  selectedCategories,
  comment,
  mode,
  isSubmitting,
  onCategoryToggle,
  onCommentChange,
  onSubmitDetails,
}) => {
  const showComment = mode === 'categories-comment';
  // In categories-only mode, selecting a chip auto-submits — no button needed
  const showSubmitButton = mode === 'categories-comment';

  return (
    <>
      <Box sx={categoriesRowSx}>
        {categories.map(category => (
          <Chip
            key={category.id}
            label={category.label}
            size="small"
            variant={selectedCategories.includes(category.id) ? 'soft' : 'outlined'}
            color={selectedCategories.includes(category.id) ? 'primary' : 'default'}
            onClick={() => onCategoryToggle(category.id)}
          />
        ))}
      </Box>

      {showComment && (
        <Box sx={commentRowSx}>
          <TextField
            size="small"
            placeholder="Tell us more (optional)"
            value={comment}
            onChange={e => onCommentChange(e.target.value)}
            multiline
            maxRows={2}
            fullWidth
            variant="outlined"
           slotProps={{ htmlInput: { maxLength: MAX_COMMENT_LENGTH } }}/>
        </Box>
      )}

      {showSubmitButton && (
        <Button
          size="small"
          variant="text"
          onClick={onSubmitDetails}
          disabled={isSubmitting || selectedCategories.length === 0}
          sx={{ mt: 1, alignSelf: 'flex-start' }}
        >
          Submit
        </Button>
      )}
    </>
  );
};

/**
 * Pure presentational component for thumbs feedback widget.
 *
 * Visual states:
 * - idle: Two thumb icon buttons
 * - categories: Expanded inline or in a popover with category chips + optional comment
 * - submitted: Check icon + "Thanks" confirmation
 */
export const ThumbsFeedback: React.FC<ThumbsFeedbackPresentationalProps> = ({
  step,
  sentiment,
  mode,
  expandMode,
  categories,
  selectedCategories,
  comment,
  compact,
  thanksMessage,
  isSubmitting,
  onThumbsUp,
  onThumbsDown,
  onCategoryToggle,
  onCommentChange,
  onSubmitDetails,
  onDismiss,
}) => {
  const thumbsDownRef = useRef<HTMLButtonElement>(null);
  const getButtonSx = compact ? compactThumbButtonSx : thumbButtonSx;
  const iconSize = compact ? 'small' : 'medium';
  const showExpansion = step === 'categories' && mode !== 'thumbs-only';

  // Hidden = thanks already dismissed
  if (step === 'hidden') return null;

  if (step === 'submitted') {
    return (
      <Box sx={thanksSx} aria-live="polite">
        <CheckCircleOutlineIcon fontSize={iconSize} />
        <Typography variant="caption">{thanksMessage}</Typography>
      </Box>
    );
  }

  const detailsProps = {
    categories,
    selectedCategories,
    comment,
    mode,
    isSubmitting,
    onCategoryToggle,
    onCommentChange,
    onSubmitDetails,
  };

  return (
    <Box sx={rootSx}>
      {/* Thumbs row */}
      <Box sx={thumbsRowSx}>
        <IconButton
          size={iconSize}
          sx={getButtonSx(sentiment === 'positive')}
          onClick={onThumbsUp}
          disabled={sentiment !== null}
          aria-label="Rate as helpful"
        >
          {sentiment === 'positive' ? (
            <ThumbUpAltIcon fontSize={iconSize} />
          ) : (
            <ThumbUpOffAltIcon fontSize={iconSize} />
          )}
        </IconButton>

        <IconButton
          ref={thumbsDownRef}
          size={iconSize}
          sx={getButtonSx(sentiment === 'negative')}
          onClick={onThumbsDown}
          disabled={sentiment !== null}
          aria-label="Rate as not helpful"
        >
          {sentiment === 'negative' ? (
            <ThumbDownAltIcon fontSize={iconSize} />
          ) : (
            <ThumbDownOffAltIcon fontSize={iconSize} />
          )}
        </IconButton>
      </Box>

      {/* Inline expansion */}
      {expandMode === 'inline' && (
        <Box sx={expansionSx(showExpansion)}>
          {showExpansion && <FeedbackDetails {...detailsProps} />}
        </Box>
      )}

      {/* Popover expansion */}
      {expandMode === 'popover' && (
        <Popover
          open={showExpansion}
          anchorEl={thumbsDownRef.current}
          onClose={onDismiss}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: popoverContentSx } }}
        >
          <FeedbackDetails {...detailsProps} />
        </Popover>
      )}
    </Box>
  );
};
